import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cx } from '../cx';
import { controlClass } from './Field';

export type AutocompleteProps<T> = {
  /** O texto escrito. Controlado por quem usa, para poder filtrar tambem uma lista ao lado. */
  value: string;
  onChange: (value: string) => void;
  /** Vai buscar as sugestoes para `query` (ja sem espacos nas pontas). */
  search: (query: string) => Promise<readonly T[]>;
  /** Escolheu uma sugestao (clique, ou Enter na sugestao marcada). */
  onSelect: (item: T) => void;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** Letras a partir das quais se procura. */
  minChars?: number;
  /** Espera depois da ultima tecla, para nao fazer um pedido por tecla. */
  delayMs?: number;
  /** Limpa o texto depois de escolher (para campos que so servem para escolher). */
  clearOnSelect?: boolean;
  emptyText?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  // O `Field` passa estes ao controlo.
  id?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
};

type Found<T> =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'done'; items: readonly T[] }
  | { state: 'error'; message: string };

/**
 * Campo de pesquisa com sugestoes (padrao combobox do WAI-ARIA): as sugestoes
 * aparecem por baixo enquanto se escreve; setas para andar, Enter para
 * escolher, Esc para fechar. Serve dentro de um `Field` como um `Input`.
 */
export function Autocomplete<T>({
  value,
  onChange,
  search,
  onSelect,
  getKey,
  renderItem,
  minChars = 2,
  delayMs = 250,
  clearOnSelect = false,
  emptyText = 'Sem resultados.',
  placeholder,
  disabled,
  autoFocus,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: AutocompleteProps<T>) {
  const generated = useId();
  const inputId = id ?? generated;
  const listId = `${inputId}-sugestoes`;
  const [found, setFound] = useState<Found<T>>({ state: 'idle' });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // A funcao de pesquisa muda a cada render de quem usa; nao deve repetir o pedido.
  const searchRef = useRef(search);
  searchRef.current = search;

  const query = value.trim();
  useEffect(() => {
    if (query.length < minChars) {
      setFound({ state: 'idle' });
      return;
    }
    let current = true;
    setFound({ state: 'loading' });
    const timer = setTimeout(() => {
      searchRef
        .current(query)
        .then((items) => {
          if (!current) return;
          setFound({ state: 'done', items });
          setActive(-1);
        })
        .catch((e: unknown) => {
          if (!current) return;
          setFound({
            state: 'error',
            message: e instanceof Error ? e.message : 'Não foi possível procurar.',
          });
        });
    }, delayMs);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [query, minChars, delayMs]);

  const items = found.state === 'done' ? found.items : [];
  const showList = open && found.state !== 'idle' && found.state !== 'loading';
  const expanded = showList && items.length > 0;

  // A lista fecha e so volta a abrir quando se escreve outra vez; quem usa pode
  // pôr no texto o que foi escolhido sem a reabrir.
  const choose = (item: T) => {
    setOpen(false);
    setActive(-1);
    if (clearOnSelect) onChange('');
    onSelect(item);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (items.length > 0) setActive((a) => (a + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      if (items.length > 0) setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (e.key === 'Enter') {
      const pick = expanded
        ? (items[active] ?? (items.length === 1 ? items[0] : undefined))
        : undefined;
      if (pick !== undefined) {
        e.preventDefault();
        choose(pick);
      }
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className={cx('relative', className)}>
      <input
        id={inputId}
        type="search"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        className={cx(controlClass, 'min-h-11')}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {/* A lista existe sempre, para o aria-controls apontar para algo. */}
      <ul
        id={listId}
        role="listbox"
        aria-label={ariaLabel ? `Sugestões: ${ariaLabel}` : 'Sugestões'}
        className={cx(
          'absolute inset-x-0 top-full z-20 mt-1 max-h-80 overflow-y-auto border-2 border-gu-line bg-gu-raised shadow-lg',
          !expanded && 'hidden',
        )}
      >
        {items.map((item, i) => (
          <li
            key={getKey(item)}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={i === active}
            className={cx(
              'cursor-pointer px-3 py-2 text-body',
              i === active ? 'bg-gu-ink/10' : 'hover:bg-gu-ink/5',
            )}
            // Sem isto o input perde o foco antes do clique e a lista fecha.
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActive(i)}
            onClick={() => choose(item)}
          >
            {renderItem(item)}
          </li>
        ))}
      </ul>
      {showList && items.length === 0 ? (
        <p
          role="status"
          className="absolute inset-x-0 top-full z-20 mt-1 border-2 border-gu-line bg-gu-raised px-3 py-2 text-body text-gu-ink-muted shadow-lg"
        >
          {found.state === 'error' ? found.message : emptyText}
        </p>
      ) : null}
    </div>
  );
}
